import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { LayoutDashboard, Wallet, Scale, FolderLock, Bot, Sparkles } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const features = [
  {
    icon: Sparkles,
    title: "Assistant fiscal IA",
    description: "Couche intelligente au-dessus d'impots.gouv",
    highlight: true,
  },
  {
    icon: FolderLock,
    title: "OCR documents",
    description: "Bulletins, avis, reçus — pré-remplissage auto"
  },
  {
    icon: Scale,
    title: "Aides détectées",
    description: "APL, RSA, bourses — montants estimés"
  },
  {
    icon: Wallet,
    title: "Abonnements",
    description: "Détection doublons, économies trouvées"
  },
  {
    icon: Bot,
    title: "Copilote 24/7",
    description: "Réponses fiscales et admin contextualisées"
  }
];

export const SolutionSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { isDark } = useTheme();

  return (
    <section 
      id="solution"
      className={`section-apple px-6 ${isDark ? 'bg-[#0a0a0a]' : 'bg-white'}`}
      data-testid="solution-section"
    >
      <div className="max-w-[980px] mx-auto">
        {/* Headline */}
        <motion.div
          ref={ref}
          className="text-center mb-16"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <h2 className={`text-[40px] sm:text-[48px] md:text-[56px] font-semibold tracking-[-0.03em] leading-[1.1] mb-6 ${
            isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'
          }`}>
            Un copilote fiscal.
            <br />
            <span className="text-[#81d8d0]">Et tout l'admin autour.</span>
          </h2>
        </motion.div>

        {/* Features grid - Apple minimal cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className={`text-center p-6 rounded-2xl transition-colors ${
                feature.highlight
                  ? isDark
                    ? 'bg-[#81d8d0]/10 border border-[#81d8d0]/30 hover:bg-[#81d8d0]/15'
                    : 'bg-[#81d8d0]/15 border border-[#81d8d0]/30 hover:bg-[#81d8d0]/20'
                  : isDark
                  ? 'bg-[#141414] hover:bg-[#1a1a1a]'
                  : 'bg-[#f5f5f7] hover:bg-[#ebebed]'
              }`}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 + index * 0.08, ease: [0.25, 0.1, 0.25, 1] }}
            >
              {/* Icon - thin, no background */}
              <feature.icon 
                size={28} 
                strokeWidth={1} 
                className="mx-auto mb-4 text-[#81d8d0]"
              />
              
              {/* Title */}
              <h3 className={`text-[15px] font-semibold tracking-[-0.01em] mb-1 ${
                isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'
              }`}>
                {feature.title}
              </h3>
              
              {/* Description */}
              <p className={`text-[13px] tracking-[-0.01em] ${
                isDark ? 'text-[#6e6e73]' : 'text-[#86868b]'
              }`}>
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;
